// SPDX-License-Identifier: Apache-2.0

// csv → markdown table.
//
// Hand-rolled parser. The OLD Tether shipped a fenced-csv codeblock; v2
// upgrades to a real GFM pipe-table so the orbit's note viewer renders rows
// without a separate codeblock-to-table step.
//
// Heuristic for delimiter: scan the first non-empty line and pick whichever of
//   `,`  `;`  `\t`
// produces the most fields (ties broken in that priority order). RFC-4180-ish
// quoting: a field may be wrapped in `"…"`, embedded `""` becomes `"`, and
// embedded delimiter / newline is preserved.

import {
  baseTitle,
  corruptResult,
  warnIfLarge,
  wrapWithTitle,
  type IngestResult,
} from './common';

const DELIMS = [',', ';', '\t'] as const;
type Delim = typeof DELIMS[number];

export async function parseCsv(file: File): Promise<IngestResult> {
  warnIfLarge(file, 'csv');
  const title = baseTitle(file.name);
  try {
    const raw = await file.text();
    if (raw.trim() === '') {
      return {
        title,
        content: wrapWithTitle(title, ''),
        front: {},
        kind: 'csv',
      };
    }
    const delim = detectDelimiter(raw);
    const rows = parseCsvRows(raw, delim);
    const table = rowsToMarkdownTable(rows);
    return {
      title,
      content: wrapWithTitle(title, table),
      front: {},
      kind: 'csv',
    };
  } catch (err) {
    return corruptResult(file.name, err instanceof Error ? err.message : String(err));
  }
}

/** Scan the first non-empty line, count occurrences of each candidate
 *  delimiter outside quoted fields, return the winner. */
export function detectDelimiter(raw: string): Delim {
  const line = firstNonEmptyLine(raw);
  if (!line) return ',';
  let best: Delim = ',';
  let bestCount = -1;
  for (const d of DELIMS) {
    const c = countUnquoted(line, d);
    // Tie-break by DELIMS order (comma > semicolon > tab).
    if (c > bestCount) {
      best = d;
      bestCount = c;
    }
  }
  return best;
}

function firstNonEmptyLine(raw: string): string {
  // We need a line that respects quoted newlines — a `\n` inside `"…"` does
  // not end the line. Walk the input and break on an unquoted newline.
  let inQuote = false;
  let start = 0;
  let i = 0;
  for (; i < raw.length; i++) {
    const c = raw[i];
    if (c === '"') {
      // Look-ahead: doubled quote = escaped quote, stays in same state.
      if (inQuote && raw[i + 1] === '"') {
        i++;
        continue;
      }
      inQuote = !inQuote;
      continue;
    }
    if (!inQuote && (c === '\n' || c === '\r')) {
      const candidate = raw.slice(start, i);
      if (candidate.trim() !== '') return candidate;
      // Skip CRLF pair.
      if (c === '\r' && raw[i + 1] === '\n') i++;
      start = i + 1;
    }
  }
  const tail = raw.slice(start);
  return tail.trim() === '' ? '' : tail;
}

function countUnquoted(s: string, d: string): number {
  let n = 0;
  let inQuote = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"') {
      if (inQuote && s[i + 1] === '"') {
        i++;
        continue;
      }
      inQuote = !inQuote;
      continue;
    }
    if (!inQuote && c === d) n++;
  }
  return n;
}

/** RFC-4180-ish row parser. Returns an array of rows, each an array of
 *  field strings. Embedded newlines inside `"…"` are preserved verbatim. */
export function parseCsvRows(raw: string, delim: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuote = false;
  let i = 0;
  const n = raw.length;
  while (i < n) {
    const c = raw[i];
    if (inQuote) {
      if (c === '"') {
        if (raw[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuote = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuote = true;
      i++;
      continue;
    }
    if (c === delim) {
      row.push(field);
      field = '';
      i++;
      continue;
    }
    if (c === '\r') {
      // Treat CRLF or bare CR as one row terminator.
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
      if (raw[i + 1] === '\n') i++;
      i++;
      continue;
    }
    if (c === '\n') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
      i++;
      continue;
    }
    field += c;
    i++;
  }
  // Flush last field/row only if the file actually had content.
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop a trailing empty row caused by a final newline.
  if (rows.length > 0) {
    const last = rows[rows.length - 1];
    if (last && last.length === 1 && last[0] === '') rows.pop();
  }
  return rows;
}

/** Render rows as a GFM pipe-table. The first row is the header. Empty
 *  input yields an empty string. */
export function rowsToMarkdownTable(rows: string[][]): string {
  if (rows.length === 0) return '';
  // Normalise width to the widest row so the table renders as a grid.
  const width = rows.reduce((m, r) => Math.max(m, r.length), 0);
  if (width === 0) return '';
  const escape = (s: string): string =>
    s.replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
  const padded = rows.map((r) => {
    const cells = r.slice();
    while (cells.length < width) cells.push('');
    return cells;
  });
  const renderRow = (cells: string[]): string =>
    `| ${cells.map(escape).map((c) => c.trim()).join(' | ')} |`;
  const header = renderRow(padded[0] ?? []);
  const sep = `|${' --- |'.repeat(width)}`;
  const body = padded.slice(1).map(renderRow);
  return [header, sep, ...body].join('\n');
}
